import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Form, Input, InputNumber, Modal, Select, Table, Tabs, Tag } from "antd";
import { CHANNEL_LABELS } from "./const";
import { getMappingColumns } from "./modules/getMappingColumns";
import { getSyncLogColumns } from "./modules/getSyncLogColumns";
import { useChannelManager } from "./modules/useChannelManager";

const { Option } = Select;
const { TabPane } = Tabs;

const ChannelManagerScreen = () => {
  const {
    rooms,
    ratePlans,
    mappings,
    syncLogs,
    logsLoading,
    channels,
    isModalVisible,
    editingMapping,
    formData,
    setIsModalVisible,
    handleFieldChange,
    handleCreate,
    handleEdit,
    handleModalOk,
    handleDelete,
    fetchSyncLogs,
    fetchChannels,
  } = useChannelManager();

  const mappingColumns = getMappingColumns({ handleEdit, handleDelete });
  const syncLogColumns = getSyncLogColumns();

  return (
    <div className="container">
      <h1>Channel Manager</h1>
      <p style={{ color: "#888" }}>
        Connect your rooms/rate plans to each OTA channel's room type/rate plan codes, then
        push availability, rates and restrictions from Smart Calendar out to every connected
        channel.
      </p>

      <Tabs defaultActiveKey="channels">
        <TabPane tab="Channels" key="channels">
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchChannels}
            style={{ marginBottom: 16 }}
          >
            Refresh
          </Button>
          <Table
            dataSource={channels}
            rowKey="channel"
            pagination={false}
            columns={[
              { title: "Channel", dataIndex: "label", key: "label" },
              {
                title: "Integration",
                dataIndex: "implemented",
                key: "implemented",
                render: (implemented) => {
                  if (implemented === true) {
                    return <Tag color="green">Implemented</Tag>;
                  }
                  if (implemented === "partial") {
                    return <Tag color="gold">Partially implemented</Tag>;
                  }
                  return <Tag color="default">Not implemented yet</Tag>;
                },
              },
              {
                title: "This property",
                dataIndex: "configured",
                key: "configured",
                render: (configured) => (
                  <Tag color={configured ? "blue" : "default"}>
                    {configured ? "Configured" : "Not configured"}
                  </Tag>
                ),
              },
            ]}
          />
          <p style={{ color: "#888", marginTop: 12 }}>
            "Not implemented yet" channels need a Connectivity Partner API integration built
            first (requires partner approval from that OTA). "Not configured" means this
            property is missing channelManager.&lt;channel&gt;.propertyCode in its tenant
            config.
          </p>
        </TabPane>

        <TabPane tab="Room / Rate Mapping" key="mappings">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            style={{ marginBottom: 16 }}
          >
            Add Mapping
          </Button>

          <Table
            dataSource={mappings}
            columns={mappingColumns}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
          />
        </TabPane>

        <TabPane tab="Sync Log" key="synclog">
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchSyncLogs}
            loading={logsLoading}
            style={{ marginBottom: 16 }}
          >
            Refresh
          </Button>

          <Table
            dataSource={syncLogs}
            columns={syncLogColumns}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
            expandable={{
              expandedRowRender: (record) => (
                <div>
                  <strong>Request:</strong>
                  <pre style={{ maxHeight: 200, overflow: "auto", background: "#f5f5f5", padding: 8 }}>
                    {record.requestPayload}
                  </pre>
                  {record.responsePayload && (
                    <>
                      <strong>Response:</strong>
                      <pre style={{ maxHeight: 200, overflow: "auto", background: "#f5f5f5", padding: 8 }}>
                        {record.responsePayload}
                      </pre>
                    </>
                  )}
                </div>
              ),
            }}
          />
        </TabPane>
      </Tabs>

      <Modal
        title={editingMapping ? "Edit Mapping" : "Add Mapping"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form layout="vertical">
          <Form.Item label="Channel" required>
            <Select
              value={formData.channel}
              onChange={(value) => handleFieldChange("channel", value)}
              disabled={!!editingMapping}
            >
              {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Room" required>
            <Select
              value={formData.room}
              onChange={(value) => handleFieldChange("room", value)}
              placeholder="Select room"
            >
              {rooms.map((room) => (
                <Option key={room._id} value={room._id}>
                  {room.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Rate Plan" required>
            <Select
              value={formData.ratePlan}
              onChange={(value) => handleFieldChange("ratePlan", value)}
              placeholder="Select rate plan"
            >
              {ratePlans.map((ratePlan) => (
                <Option key={ratePlan._id} value={ratePlan._id}>
                  {ratePlan.rateplanname}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Room Type Code (channel-side room type identifier)" required>
            <Input
              value={formData.roomTypeCode}
              onChange={(e) => handleFieldChange("roomTypeCode", e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Rate Plan Code (channel-side rate plan identifier)" required>
            <Input
              value={formData.ratePlanCode}
              onChange={(e) => handleFieldChange("ratePlanCode", e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Room Type Name (shown to the channel)" required>
            <Input
              value={formData.roomTypeName}
              onChange={(e) => handleFieldChange("roomTypeName", e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Rate Plan Name (shown to the channel)" required>
            <Input
              value={formData.ratePlanName}
              onChange={(e) => handleFieldChange("ratePlanName", e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Included Adult Occupancy" required>
            <InputNumber
              min={1}
              value={formData.includedAdultOccupancy}
              onChange={(value) => handleFieldChange("includedAdultOccupancy", value)}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item label="Rate Setup Type" required>
            <Select
              value={formData.rateSetupType}
              onChange={(value) => handleFieldChange("rateSetupType", value)}
            >
              <Option value="Manual">Manual</Option>
              <Option value="Derived">Derived</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ChannelManagerScreen;
